import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@wtc/ui";
import { z } from "zod";
import { MessageBaseSchema, type Message } from "@/lib/core/crm/types";
import { logger } from "@/lib/logger";

interface UseMessageSearchOptions {
  provider: string;
  connectionId: string;
  searchQuery: string;
  filter?: string;
  limit?: number;
  skip?: number;
  enabled?: boolean;
}

interface MessageSearchResult {
  messages: Message[];
  totalMessages: number;
  hasMore: boolean;
  searchQuery: string;
  filter: string;
}

/**
 * Wire shape of ONE searched message.
 *
 * Deliberately `MessageBaseSchema` with its FORMAT refinements relaxed rather
 * than a hand-listed schema: extending keeps key-for-key parity with the
 * canonical `Message` (a new required field can't be forgotten here), while the
 * relaxations below reflect what the provider transforms genuinely emit —
 * `webLink: ""` for an Outlook message with no link, a connection id (not a
 * UUID) for `inboxId`, and an empty sender address for mail with no parseable
 * From. Parsing with the canonical schema instead would reject nearly every
 * real message and silently empty the search results.
 */
const SearchedMessageSchema = MessageBaseSchema.extend({
  sender: z.object({
    name: z.string(),
    email: z.string(),
    avatar: z.string().optional(),
  }),
  recipients: z.array(z.object({ name: z.string(), email: z.string() })),
  replyTo: z.array(z.object({ name: z.string(), email: z.string() })).optional(),
  ccRecipients: z.array(z.object({ name: z.string(), email: z.string() })).optional(),
  bccRecipients: z.array(z.object({ name: z.string(), email: z.string() })).optional(),
  receivedDateTime: z.string(),
  sentDateTime: z.string(),
  webLink: z.string().optional(),
  inboxId: z.string(),
}).passthrough();

/** Envelope returned by `GET /api/connections/messages/search` for every provider. */
const SearchResponseSchema = z.object({
  messages: z.array(z.unknown()).default([]),
  totalMessages: z.number().int().nonnegative().default(0),
  hasMore: z.boolean().default(false),
  searchQuery: z.string().optional(),
  filter: z.string().optional(),
});

const ErrorResponseSchema = z.object({ error: z.string().optional() });

/**
 * Search ONE mailbox.
 *
 * Every provider — Outlook included — goes through
 * `/api/connections/messages/search`. Microsoft mailboxes used to be routed to
 * `/api/debug/msft-emails`, a route named "debug" that answered a different
 * response shape (`data`/`total`) and read tokens from a different store; the
 * real route runs a Graph `$search` for `outlook-oauth` connections, so the
 * fork bought nothing and split the contract in two.
 */
export function useMessageSearch({
  provider,
  connectionId,
  searchQuery,
  filter = "all",
  limit = 25,
  skip = 0,
  enabled = true,
}: UseMessageSearchOptions) {
  const { getAuthToken } = useAuth();

  return useQuery({
    queryKey: ["message-search", provider, connectionId, searchQuery, filter, skip, limit],
    queryFn: async (): Promise<MessageSearchResult> => {
      const token = getAuthToken();

      const params = new URLSearchParams({
        provider,
        connectionId,
        query: searchQuery,
        filter,
        limit: String(limit),
        skip: String(skip),
      });

      const response = await fetch(`/api/connections/messages/search?${params}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      const payload: unknown = await response.json().catch(() => null);

      if (!response.ok) {
        const parsedError = ErrorResponseSchema.safeParse(payload);
        throw new Error(
          (parsedError.success ? parsedError.data.error : undefined) || "Failed to search messages"
        );
      }

      const parsed = SearchResponseSchema.safeParse(payload);
      if (!parsed.success) {
        logger.error("Message search returned an unreadable response", parsed.error, {
          op: "useMessageSearch",
          provider,
        });
        throw new Error("Failed to search messages");
      }

      const messages: Message[] = [];
      let unreadable = 0;
      for (const candidate of parsed.data.messages) {
        const parsedMessage = SearchedMessageSchema.safeParse(candidate);
        if (parsedMessage.success) {
          messages.push(parsedMessage.data);
        } else {
          unreadable += 1;
        }
      }
      if (unreadable > 0) {
        // Never silent: a message the inbox cannot render is a provider-transform
        // problem worth seeing, but it must not blank out the whole result set.
        logger.warn("Message search dropped messages that failed validation", {
          op: "useMessageSearch",
          provider,
          droppedCount: unreadable,
          keptCount: messages.length,
        });
      }

      return {
        messages,
        totalMessages: parsed.data.totalMessages,
        hasMore: parsed.data.hasMore,
        searchQuery: parsed.data.searchQuery ?? searchQuery,
        filter: parsed.data.filter ?? filter,
      };
    },
    enabled: enabled && Boolean(provider && connectionId && searchQuery),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
  });
}
